import type Phaser from 'phaser';
import { hash2 } from '../core/rng';
import type { BiomeId } from '../world/biome';
import { PAINTED, PROP_WIDTH, SHARED_PROPS, propFor, propKey, type PropRole } from './artAssets';
import type { PropSource } from './MapRenderer';

export { preloadPainted } from './artAssets';

/** Which prop roles stand in for each procedural decoration frame. */
const ROLES_FOR: Record<string, readonly PropRole[]> = {
  pine: ['tallTree', 'smallTree', 'tallTree'],
  oak: ['roundTree', 'roundTree', 'tallTree'],
  deadtree: ['deadTree'],
  bush: ['bush', 'bushes'],
  boulder: ['boulder', 'rocks'],
  reeds: ['tuft'],
};

/**
 * Painted decorations (D133): a deterministic choice of sprite per tile
 * (role variety, a little jitter, mirrored half the time) so forests read as
 * natural clusters. Returns null where no painted sprite exists.
 */
export function paintedProps(scene: Phaser.Scene, biome: BiomeId | undefined): PropSource | null {
  const b = biome ?? 'valley';
  const a = PAINTED[b];
  if (!a) return null;
  const atlas = packAtlas(scene, `paint-props-atlas-${b}`, (k) => k.startsWith(`paint-prop-${b}-`) || k.startsWith('paint-prop-ruins-') || k.startsWith('paint-prop-infected-'));
  const has = (k: string): boolean => atlas.frames.has(k);
  const out = (key: string, width: number, rest: { dx: number; dy: number; flip: boolean; flat: boolean }): ReturnType<PropSource> => ({ key: atlas.key, frame: key, width, ...rest });
  return (frame, x, y) => {
    const r = hash2(x, y, 4401);
    const jitter = { dx: (hash2(x, y, 4402) - 0.5) * 10, dy: (hash2(x, y, 4403) - 0.5) * 5, flip: hash2(x, y, 4404) < 0.5 };
    if (frame === 'rubble' && SHARED_PROPS.ruins > 0) {
      const key = propKey('ruins', Math.floor(r * SHARED_PROPS.ruins));
      return has(key) ? out(key, fitWidth(atlas, key, 52, 40), { ...jitter, flat: false }) : null;
    }
    if (frame === 'infestation' && SHARED_PROPS.infected > 0) {
      const key = propKey('infected', [2, 5, 6][Math.floor(r * 3)]! % SHARED_PROPS.infected);
      return has(key) ? out(key, fitWidth(atlas, key, 40, 26), { ...jitter, flat: true }) : null;
    }
    const roles = ROLES_FOR[frame];
    if (!roles) return null;
    const pick = propFor(b, roles[Math.floor(r * roles.length)]!, hash2(x, y, 4406));
    if (!pick || !has(pick.key)) return null;
    const scale = 0.85 + hash2(x, y, 4405) * 0.3;
    return out(pick.key, fitWidth(atlas, pick.key, PROP_WIDTH[pick.role] * scale, PROP_WIDTH[pick.role] * scale * 1.7), { ...jitter, flat: false });
  };
}

/** Display width that fits a sprite inside maxW × maxH world px (keeps tall, narrow sprites from towering). */
function fitWidth(atlas: Atlas, key: string, maxW: number, maxH: number): number {
  const src = atlas.frames.get(key)!;
  const k = Math.min(maxW / src.w, maxH / src.h);
  return src.w * k;
}

interface Atlas {
  readonly key: string;
  readonly frames: Map<string, { w: number; h: number }>;
}

/**
 * Pack every loaded prop image into ONE canvas texture (shelf packing), so the
 * whole forest draws in a single batch instead of one texture switch per tree.
 */
function packAtlas(scene: Phaser.Scene, key: string, take: (k: string) => boolean): Atlas {
  const frames = new Map<string, { w: number; h: number }>();
  const keys = scene.textures.getTextureKeys().filter(take).sort();
  if (scene.textures.exists(key)) {
    for (const k of keys) {
      const f = scene.textures.getFrame(key, k);
      if (f) frames.set(k, { w: f.width, h: f.height });
    }
    return { key, frames };
  }
  const W = 2048;
  const pad = 2;
  let x = 0;
  let y = 0;
  let rowH = 0;
  const place: { k: string; img: CanvasImageSource; w: number; h: number; x: number; y: number }[] = [];
  for (const k of keys) {
    const img = scene.textures.get(k).getSourceImage() as HTMLImageElement;
    const w = img.width;
    const h = img.height;
    if (x + w > W) {
      x = 0;
      y += rowH + pad;
      rowH = 0;
    }
    place.push({ k, img, w, h, x, y });
    x += w + pad;
    rowH = Math.max(rowH, h);
  }
  const H = Math.max(1, y + rowH);
  const tex = scene.textures.createCanvas(key, W, H);
  if (!tex) return { key, frames };
  const ctx = tex.getContext();
  for (const p of place) {
    ctx.drawImage(p.img, p.x, p.y);
    tex.add(p.k, 0, p.x, p.y, p.w, p.h);
    frames.set(p.k, { w: p.w, h: p.h });
  }
  tex.refresh();
  return { key, frames };
}

/** Average colour of a loaded texture as #rrggbb, darkened by `shade` (0..1). */
export function averageColour(scene: Phaser.Scene, key: string, shade = 0): string | null {
  if (!scene.textures.exists(key)) return null;
  const img = scene.textures.get(key).getSourceImage() as CanvasImageSource;
  const c = document.createElement('canvas');
  c.width = c.height = 8;
  const g = c.getContext('2d');
  if (!g) return null;
  g.drawImage(img, 0, 0, 8, 8);
  const d = g.getImageData(0, 0, 8, 8).data;
  let r = 0, gg = 0, b = 0;
  for (let i = 0; i < d.length; i += 4) {
    r += d[i]!;
    gg += d[i + 1]!;
    b += d[i + 2]!;
  }
  const n = d.length / 4;
  const k = 1 - shade;
  const hx = (v: number): string => Math.round(Math.max(0, Math.min(255, (v / n) * k))).toString(16).padStart(2, '0');
  return `#${hx(r)}${hx(gg)}${hx(b)}`;
}
